import { sidebarOpenTabAtom } from '@/atoms/persistedPreferencesAtoms';
import { ChatSidebar } from '@/components/chat/components/ChatSidebar';
import {
  IS_INSET_DESIGN_ENABLED,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/components/constants/flags';
import { useIsAgentHomeV1Enabled, useIsProjectsEnabled } from '@/features/flags';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { ChatSidebarWork } from './ChatSidebarWork';
import { useChatSidebarHandlers } from './hooks/useChatSidebarHandlers';
import { OrganizationSideBar } from './organization/side-bar';

/**
 * Other Sidebar Panels Component
 * Handles rendering of OrganizationSideBar and ChatSidebar. Only shows when
 * SessionsBar is not open. Notifications live in the popover in
 * `NotificationsPopoverContent` (desktop) and the mobile sheet — neither
 * renders here. Settings navigation uses the global settings modal.
 */
export const OtherSidebarPanels = () => {
  const { isSessionlessEvidenceShown: enableSessionlessEvidence } = useEvidenceAccess();
  // Work mounts the same chat sidebar (Routines + recent Work chats) even when
  // the user has no Evidence entitlement — Work and Evidence are independent.
  const isWorkEnabled = useIsProjectsEnabled();
  const isAgentHomeV1Enabled = useIsAgentHomeV1Enabled();
  const router = useRouter();
  const isMobile = useIsMobile();
  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  const otherSidebarOpen = !isMobile && Boolean(sidebarOpenTab);
  const isInsetDesign = IS_INSET_DESIGN_ENABLED();

  // Chat sidebar handlers (shared with Sidebar index.tsx)
  const { currentChatId, handleSelectChat, handleNewChat, handleDeleteChat } =
    useChatSidebarHandlers();

  return (
    <>
      {/* Evidence / Work Chat Sidebar - shows on all routes when toggled */}
      {/* Disabled when new SidebarPanel is enabled (evidence content moved there) */}
      {!isAgentHomeV1Enabled &&
        !IS_SIDEBAR_PANEL_ENABLED() &&
        (enableSessionlessEvidence || isWorkEnabled) && (
          <ChatSidebarWork>
            {(work) => (
              <ChatSidebar
                onSelectChat={handleSelectChat}
                onNewChat={handleNewChat}
                onDeleteChat={handleDeleteChat}
                currentChatId={currentChatId}
                work={work}
              />
            )}
          </ChatSidebarWork>
        )}

      {/* OrganizationSideBar - Always render for animations to behave correctly */}
      <aside
        className={cn(
          'hidden h-full flex-col overflow-x-hidden duration-500 sm:flex',
          isInsetDesign ? 'py-3' : 'gap-2 border-r py-4',
          !otherSidebarOpen &&
            router.pathname.includes('/organization') &&
            !router.pathname.includes('/settings')
            ? cn('w-[200px]', isInsetDesign && 'me-2')
            : cn('w-0', !isInsetDesign && 'border-r-0')
        )}
        onClick={(e) => {
          e.stopPropagation();
        }}
        data-testid="sidebar-organization-panel"
      >
        {isInsetDesign ? (
          <div className="h-full overflow-hidden rounded-lg bg-surface py-2">
            <OrganizationSideBar />
          </div>
        ) : (
          <OrganizationSideBar />
        )}
      </aside>
    </>
  );
};
